import { Component, Inject, OnInit, signal, HostListener } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { AppService } from '@app/app.service';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { ShowEmailDialogComponent } from '@app/shared/show-email-dialog/show-email-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  selector: 'app-truck-view-public-dialog',
  imports: [
    MatDialogModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCardModule,
    CommonModule,
    CurrencyPipe,
    DatePipe,
    ClipboardModule,
    MatChipsModule,
    MatExpansionModule,
  ],
  templateUrl: './truck-view-public-dialog.component.html',
  styleUrl: './truck-view-public-dialog.component.scss',
})
export class TruckViewPublicDialogComponent implements OnInit {
  truckId: number | null = null;
  truck = signal<any>(null);
  loading = signal(false);

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<TruckViewPublicDialogComponent>,
    private appService: AppService,
    private clipboard: Clipboard,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.truckId = this.data?.truck?.id;

    if (!this.truckId) {
      return;
    }

    this.getTruck();
  }

  getTruck(): void {
    this.loading.set(true);

    this.appService.get(`trucks/view/${this.truckId}`).subscribe({
      next: (response: any) => {
        this.truck.set(response.data);
        this.loading.set(false);
      },
      error: () => {
        this.truck.set(null);
        this.loading.set(false);
      },
    });
  }

  openInNewTab(): void {

    window.open(
      `${window.location.origin}/trucks/view/${this.truckId}`,
      '_blank'
    );

    this.dialogRef.close();
  }

toggleSaved(): void {

    const originalValue = this.truck().is_saved;

    this.truck.update(current => ({
      ...current,
      is_saved: !current.is_saved,
    }));
    
    // Trigger the parent component to update its saved trucks list
    this.data.toggleSavedTruck();
  }

  formatPhone(phone: string | null | undefined): string {
    if (!phone) {
      return '';
    }

    const digits = phone.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    return phone;
  }

  toFeetAndInches(totalInches: number): string {
    if (!totalInches) {
      return '—';
    }

    const feet = Math.floor(totalInches / 12);
    const inches = totalInches % 12;

    return `${feet}-${inches}`;
  }

  getCarrierType(carrier: any): string {
    const isCarrier = carrier?.is_carrier ?? false;
    const isBroker = carrier?.is_broker ?? false;

    if (isBroker && isCarrier) {
      return 'BROKER & CARRIER';
    }

    if (isBroker) {
      return 'BROKER';
    }

    if (isCarrier) {
      return 'CARRIER';
    }

    return '';
  }

  copyLink(): void {
    const id = this.truckId ?? this.data?.truck?.id;

    if (!id) {
      return;
    }

    const url = `${window.location.origin}/trucks/view/${id}`;

    this.clipboard.copy(url);
    this.appService.showSnackBar('Link copied to clipboard.', 'success');
  }

  openMotus(): void {
    const USDOTNumber = this.truck().carrier?.dot_number;
    const url = `https://motus.dot.gov/customer/${USDOTNumber}/account`;
    window.open(url, '_blank');
  }

  showUserEmail(): void {
    this.dialog.open(ShowEmailDialogComponent, {
      width: '400px',
      data: {
        url: `trucks/view/${this.truckId}/email`,
        truck: this.truck(),
      },
    });
  }

  get tags(): string[] {
    const truck = this.truck();

    if (!truck) {
      return [];
    }

    const tagMap: Record<string, string> = {
      tarp: 'Tarp',
      hazmat: 'Hazmat',
      military_base_access: 'Military Base Access',
      twic: 'TWIC',
      emodal: 'eModal',
      outriggers: 'Outriggers',
      beam_trailer: 'Beam Trailer',
      extendable_trailer: 'Extendable Trailer',
      team: 'Team',
    };

    return Object.entries(tagMap)
      .filter(([key]) => truck[key])
      .map(([, label]) => label);
  }
}